import React, { useContext, useMemo, useState } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import styled, { useTheme } from 'styled-components';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import { useField, useForm } from 'react-final-form';
import { Button, UniformCard } from '@ubeya/shared-web/components';
import useUniforms from '@ubeya/shared/hooks/account/useUniforms';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import { InstructionsModal } from './InstructionsModal';

const Container = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  padding: 12px;
  margin: 25px 0 10px;

  h3 {
    margin-bottom: 12px;
    font-size: 14px;
    line-height: 1.5;
  }

  .instruction-line:not(:last-child) {
    border-bottom: ${({ theme }) => `1px solid ${theme.colors.grey1}`};
    padding-bottom: 10px;
  }
`;

const InstructionLine = styled.p.attrs(() => ({ className: 'instruction-line' }))`
  display: flex;

  font-size: 14px;
  line-height: 1.5;
  font-weight: 300;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey1};
  margin-bottom: 3px;

  flex-direction: column;
  gap: 5px;
`;

const InstructionLabel = styled.span`
  width: 110px;
  font-weight: 400;
`;

const InstructionValue = styled.span`
  border-radius: 5px;
  ${({ $isChanged, theme }) => ($isChanged ? `background: ${theme.colors.primary}26` : '')};
  width: fit-content;
`;

const EditInstructionsButton = styled(Button)`
  padding: 7px 16px;
`;

const trim = (value) => {
  if (typeof value === 'string') {
    return value.trim();
  }
  return value;
};

export const OrderInstructions = ({
  branchId,
  modalTitle,
  orderDetails,
  highlightChanges = false,
  enableEdit = false,
  buttonText = 'orderDetailsModalEditInstructions',
  title = 'orderDetailsModalAddedInstructions',
  includeLocation = false,
  includeUniform = false,
  includePoNumber = false,
  showNonEmptyOnly = false
}) => {
  const { t } = useContext(ConfigContext);
  const [showInstructionsModal, setShowInstructionsModal] = useState();
  const { colors } = useTheme();

  const { mappedLocations } = useLocations(branchId);
  const { mappedUniforms } = useUniforms(branchId);
  const form = useForm();

  const {
    input: { value: locationIdValue }
  } = useField('locationId');
  const {
    input: { value: uniformValue }
  } = useField('uniforms');
  const {
    input: { value: instructionsValue }
  } = useField('instructions');
  const {
    input: { value: poNumberValue }
  } = useField('poNumber');
  const {
    input: { value: meetingSpotValue }
  } = useField('meetingSpot');
  const {
    input: { value: onsiteContactNameValue }
  } = useField('onsiteContactName');
  const {
    input: { value: onsiteContactPhoneValue }
  } = useField('onsiteContactPhone');

  const showFields = {
    location: includeLocation && (!showNonEmptyOnly || !!locationIdValue),
    meetingSpot: !showNonEmptyOnly || !!trim(meetingSpotValue),
    onsiteContactName: !showNonEmptyOnly || !!trim(onsiteContactNameValue),
    onsiteContactPhone: !showNonEmptyOnly || !!trim(onsiteContactPhoneValue),
    uniform: !showNonEmptyOnly || !!trim(uniformValue),
    instructions: !showNonEmptyOnly || !!trim(instructionsValue),
    poNumber: !showNonEmptyOnly || !!trim(poNumberValue)
  };

  const showDetails = Object.values(showFields).includes(true);

  const { name: uniformName, image: uniformImage = null, description: uniformDescription = '' } = useMemo(() => {
    if (form.getState().dirtyFields.uniforms) {
      return mappedUniforms[uniformValue] || {};
    }

    if (orderDetails?.current) {
      return orderDetails.current?.uniform || {};
    }

    return orderDetails?.uniform || mappedUniforms[uniformValue] || {};
  }, [form, mappedUniforms, orderDetails, uniformValue]);

  return (
    <>
      {showDetails && (
        <Container>
          <h3>{t(title)}</h3>
          {showFields.location && (
            <InstructionLine>
              <InstructionLabel>{t('location')}</InstructionLabel>
              <InstructionValue $isChanged={highlightChanges && !!form.getState().dirtyFields.locationId}>
                {mappedLocations[locationIdValue]?.address}
              </InstructionValue>
            </InstructionLine>
          )}
          {showFields.meetingSpot && (
            <InstructionLine>
              <InstructionLabel>{t('meetingSpot')}</InstructionLabel>
              <InstructionValue $isChanged={highlightChanges && !!form.getState().dirtyFields.meetingSpot}>
                {meetingSpotValue}
              </InstructionValue>
            </InstructionLine>
          )}
          {showFields.onsiteContactName && (
            <InstructionLine>
              <InstructionLabel>{t('onsiteContact')}</InstructionLabel>
              <InstructionValue $isChanged={highlightChanges && !!form.getState().dirtyFields.onsiteContactName}>
                {onsiteContactNameValue}
              </InstructionValue>
            </InstructionLine>
          )}
          {showFields.onsiteContactPhone && (
            <InstructionLine>
              <InstructionLabel>{t('onsiteContactPhone')}</InstructionLabel>
              <InstructionValue $isChanged={highlightChanges && !!form.getState().dirtyFields.onsiteContactPhone}>
                {onsiteContactPhoneValue}
              </InstructionValue>
            </InstructionLine>
          )}

          {showFields.uniform && (
            <InstructionLine>
              <InstructionLabel>{t('uniform')}</InstructionLabel>
              <UniformCard
                label={uniformName}
                image={uniformImage}
                description={uniformDescription}
                //if changed, highlight the border
                labelStyle={
                  highlightChanges && !!form.getState().dirtyFields.uniforms
                    ? {
                        backgroundColor: colors.orangeLight
                      }
                    : {}
                }
                wrapperStyle={{
                  background: colors.grey1,
                  borderRadius: 4,
                  padding: 8,
                  marginTop: 5
                }}
              />
            </InstructionLine>
          )}

          {showFields.instructions && (
            <InstructionLine>
              <InstructionLabel>{t('instructions')}</InstructionLabel>
              <InstructionValue $isChanged={highlightChanges && !!form.getState().dirtyFields.instructions}>
                {orderDetails?.instructions || instructionsValue}
              </InstructionValue>
            </InstructionLine>
          )}
          {showFields.poNumber && (
            <InstructionLine>
              <InstructionLabel>{t('poNumber')}</InstructionLabel>
              <InstructionValue $isChanged={highlightChanges && !!form.getState().dirtyFields.poNumber}>
                {poNumberValue || orderDetails?.poNumber}
              </InstructionValue>
            </InstructionLine>
          )}
        </Container>
      )}

      {enableEdit && (
        <EditInstructionsButton
          secondary
          type="button"
          onClick={() => {
            setShowInstructionsModal(true);
            trackEvent('Opened order edit instructions', {
              title: t(buttonText),
              module: 'Scheduling',
              context: 'Order Instructions',
              projectId: orderDetails?.projectId,
              branchId: orderDetails?.branchId,
              entryPoint: 'order_instructions'
            });
          }}>
          {t(buttonText)}
        </EditInstructionsButton>
      )}

      {showInstructionsModal && (
        <InstructionsModal
          onClose={() => setShowInstructionsModal(false)}
          includeLocation={includeLocation}
          includeUniform={includeUniform}
          includePoNumber={includePoNumber}
          enableUniformChange
          title={modalTitle}
        />
      )}
    </>
  );
};
